import React, { useMemo } from 'react';
import diacritics from 'diacritics';
import { Breakpoint } from '@moda/om';
import { groupBy, sortBy } from 'ramda';
import { PageFilterNameSlugFacetFragment } from '../../generated/types';
import { hrefFor } from '../../routers';
import { usePLPParams } from '../../hooks/usePLPParams';
import { GridLoading } from '../Grid';
import { AlphabeticalNav, ALPHABET, NUMERIC, NAV_OPTIONS } from '../AlphabeticalNav';
import { AlphabeticalColumns, AlphabeticalColumnsButton } from '../AlphabeticalColumns';
import './DesignerListingResults.scss';

interface Props {
  designers: PageFilterNameSlugFacetFragment[];
  loading: boolean;
}

const groupByDesignerName = groupBy((designer: PageFilterNameSlugFacetFragment) => {
  const firstLetter = diacritics.remove(designer.name?.substring(0, 1).toUpperCase() || '#');

  if (ALPHABET.includes(firstLetter)) {
    return firstLetter;
  }

  return NUMERIC;
});

const createColumnId = (symbol: string) => `AlphabeticalColumn__${symbol}`;

const scrollSymbolIntoView = (symbol: string) => {
  const element = document.getElementById(`${createColumnId(symbol)}`);

  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const DesignerListingResults: React.FC<Props> = ({ designers, loading }) => {
  const {
    params: { vertical = 'women' }
  } = usePLPParams();

  const groupedDesigners = useMemo(() => groupByDesignerName(designers), [designers]);

  const activeDesigners = designers.filter(designer => designer.totalItems !== 0);

  return (
    <div className="DesignerListingResults">
      <Breakpoint gt="md">
        <AlphabeticalNav
          total={activeDesigners.length}
          alphabet={Object.keys(groupedDesigners)}
          onClick={scrollSymbolIntoView}
        />
      </Breakpoint>

      {NAV_OPTIONS.map(
        symbol =>
          groupedDesigners[symbol] && (
            <AlphabeticalColumns key={symbol} header={symbol} id={createColumnId(symbol)}>
              {sortBy(
                designer => diacritics.remove(designer.name).toLowerCase(),
                groupedDesigners[symbol] ?? []
              ).map(
                designer =>
                  designer.slug &&
                  designer.name &&
                  designer.totalItems !== 0 && (
                    <AlphabeticalColumnsButton
                      key={designer.slug}
                      to={hrefFor.DesignerPage({ designerSlug: designer.slug, vertical })}
                    >
                      {designer.name}
                    </AlphabeticalColumnsButton>
                  )
              )}
            </AlphabeticalColumns>
          )
      )}
      <GridLoading show={loading} />
    </div>
  );
};
